import React, { useEffect } from 'react'
import { useParams, useHistory, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Box, Button, Typography } from '@the-platform-group/component-library'

import Spinner from 'components/Spinner'
import { FormPaper } from 'components/Paper'

import PromptFormFields from './components/PromptFormFields'
import { useAgenticPrompt } from './hooks/useAgenticPrompt'
import { useUpsertAgenticPrompt } from './hooks/useUpsertAgenticPrompt'

const defaultValues = {
  title: '',
  body: '',
  categories: [],
  status: 'draft',
  isChild: false,
  parentPrompt: null,
}

function toFormValues(prompt) {
  if (!prompt) return defaultValues
  return {
    title: prompt.title ?? '',
    body: prompt.body ?? '',
    categories: prompt.categories ?? [],
    status: prompt.status ?? 'draft',
    isChild: !!prompt.isChild,
    parentPrompt: prompt.parentPromptId
      ? { id: prompt.parentPromptId, name: prompt.parentPromptTitle ?? '' }
      : null,
  }
}

function toApiPayload(values, { id } = {}) {
  return {
    id,
    title: values.title.trim(),
    body: values.body,
    categories: values.categories,
    status: values.status,
    isChild: values.isChild,
    // Admin-created prompts are always system-level (no user_id) so they
    // appear globally for all users of the assistant.
    isSystem: true,
    parentPromptId: values.isChild ? values.parentPrompt?.id ?? null : null,
  }
}

const AgenticPromptForm = () => {
  const { id } = useParams()
  const history = useHistory()
  const isNew = !id

  const { prompt, isLoading } = useAgenticPrompt(id)
  const { savePrompt, isSaving } = useUpsertAgenticPrompt({
    onSuccess: () => history.push('/ai-prompts'),
  })

  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (!isNew && prompt) {
      reset(toFormValues(prompt))
    }
  }, [isNew, prompt, reset])

  if (!isNew && isLoading) {
    return <Spinner />
  }

  const onSubmit = values => savePrompt(toApiPayload(values, { id }))

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h3">{isNew ? 'New AI prompt' : 'Edit AI prompt'}</Typography>
        <Button component={Link} to="/ai-prompts" variant="text">
          Back to list
        </Button>
      </Box>

      <FormPaper>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <PromptFormFields control={control} formState={formState} promptId={id} />

          <Box mt={3} display="flex" gap={1} justifyContent="flex-end">
            <Button component={Link} to="/ai-prompts" variant="text" disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save prompt'}
            </Button>
          </Box>
        </form>
      </FormPaper>
    </>
  )
}

export default AgenticPromptForm
